import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const STEPS = ["Vous deux", "La date", "Le lieu", "L'ampleur"];

export default function Onboarding() {
  const nav = useNavigate();
  const { user, loadMe, refreshWedding } = useAuth();
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (user && (user.role === "VENDOR_OWNER" || user.vendor_id)) nav("/vendor", { replace: true });
  }, [user, nav]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    partner1_first_name: "", partner2_first_name: "", wedding_date: "", city: "",
    estimated_guests: 100, estimated_budget: 35000, currency: "CHF", style: "", venue_booked: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const finish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/weddings/onboard", f);
      await loadMe(); refreshWedding();
      toast.success("Votre espace mariage est prêt ✨");
      nav("/app");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setLoading(false); }
  };

  const next = () => { if (step < STEPS.length - 1) setStep(step + 1); else finish(); };
  const canNext = step === 0 ? f.partner1_first_name : true;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                i < step ? "bg-sage text-white" : i === step ? "bg-stone-900 text-white" : "bg-stone-200 text-stone-400"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}</div>
              {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? "bg-sage" : "bg-stone-200"}`} />}
            </div>
          ))}
        </div>
        <div className="card-lux p-8 animate-fade-up" key={step}>
          <div className="eyebrow mb-2">Étape {step + 1} / {STEPS.length}</div>
          {step === 0 && (<>
            <h2 className="font-serif text-2xl mb-6">Qui se marie ?</h2>
            <div className="space-y-4">
              <div><Label>Prénom du/de la partenaire 1</Label>
                <Input data-testid="onb-p1" value={f.partner1_first_name} onChange={(e) => set("partner1_first_name", e.target.value)} className="mt-1.5 h-11" autoFocus /></div>
              <div><Label>Prénom du/de la partenaire 2</Label>
                <Input data-testid="onb-p2" value={f.partner2_first_name} onChange={(e) => set("partner2_first_name", e.target.value)} className="mt-1.5 h-11" /></div>
            </div>
          </>)}
          {step === 1 && (<>
            <h2 className="font-serif text-2xl mb-6">Quand aura lieu le grand jour ?</h2>
            <Label>Date du mariage (vous pourrez la modifier)</Label>
            <Input data-testid="onb-date" type="date" value={f.wedding_date} onChange={(e) => set("wedding_date", e.target.value)} className="mt-1.5 h-11" />
          </>)}
          {step === 2 && (<>
            <h2 className="font-serif text-2xl mb-6">Où vous marierez-vous ?</h2>
            <div className="space-y-4">
              <div><Label>Ville / région</Label>
                <Input data-testid="onb-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Lausanne, Vaud" className="mt-1.5 h-11" /></div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={f.venue_booked} onChange={(e) => set("venue_booked", e.target.checked)} className="w-4 h-4" />
                <span className="text-sm text-stone-600">Notre lieu est déjà réservé</span></label>
            </div>
          </>)}
          {step === 3 && (<>
            <h2 className="font-serif text-2xl mb-6">Quelle ampleur imaginez-vous ?</h2>
            <div className="space-y-4">
              <div><Label>Nombre d'invités estimé : <b>{f.estimated_guests}</b></Label>
                <input type="range" min="10" max="400" step="10" value={f.estimated_guests}
                  onChange={(e) => set("estimated_guests", +e.target.value)} className="w-full mt-2" data-testid="onb-guests" /></div>
              <div><Label>Budget global estimé (CHF)</Label>
                <Input data-testid="onb-budget" type="number" value={f.estimated_budget} onChange={(e) => set("estimated_budget", +e.target.value)} className="mt-1.5 h-11" /></div>
            </div>
          </>)}
          <div className="flex items-center justify-between mt-8">
            <Button variant="ghost" onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
            <Button data-testid="onb-next" onClick={next} disabled={!canNext || loading} className="rounded-full px-6">
              {step === STEPS.length - 1 ? (loading ? "Création…" : "Créer mon mariage") : "Continuer"}
              {step < STEPS.length - 1 && <ArrowRight className="w-4 h-4 ml-1" />}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
